import { api } from './api';

/**
 * Chat (M-chat) buyer-side client. Polling-first: the inbox + bell poll slowly
 * (handled by the components' refetchInterval), the open thread fast. All
 * endpoints are buyer-scoped on the server to `req.user.sub`.
 */

export type SellerKind = 'house' | 'merchant' | 'creator';
export type SenderKind = 'buyer' | 'seller';

export type ConversationListItem = {
  id: string;
  sellerKind: SellerKind;
  /** Display name of the other side (merchant business name; null = house). */
  counterparty: string | null;
  productId: string | null;
  orderId: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageSenderKind: SenderKind | null;
  unread: boolean;
};

export type ConversationsPage = { items: ConversationListItem[]; nextCursor: string | null };

export type ChatMessage = {
  id: string;
  senderKind: SenderKind;
  senderUserId: string | null;
  body: string;
  createdAt: string;
};

export type MessagesPage = { items: ChatMessage[]; nextCursor: string | null };

export async function listConversations(
  args: { limit?: number; cursor?: string } = {},
): Promise<ConversationsPage> {
  const res = await api.get<ConversationsPage>('/me/conversations', { params: args });
  return res.data;
}

export async function getChatUnreadCount(): Promise<{ count: number }> {
  const res = await api.get<{ count: number }>('/me/conversations/unread-count');
  return res.data;
}

export async function listChatMessages(
  conversationId: string,
  args: { limit?: number; cursor?: string } = {},
): Promise<MessagesPage> {
  const res = await api.get<MessagesPage>(`/me/conversations/${conversationId}/messages`, {
    params: args,
  });
  return res.data;
}

/**
 * Resolve the seller from a product / merchant / creator / order and upsert the
 * thread. A third-party creator's COURSE product resolves to that creator; a
 * HOUSE creator's course resolves to the house inbox.
 */
export async function startConversation(input: {
  productId?: string;
  merchantUserId?: string;
  creatorUserId?: string;
  orderId?: string;
}): Promise<{ conversationId: string }> {
  const res = await api.post<{ conversationId: string }>('/me/conversations/start', input);
  return res.data;
}

export async function sendChatMessage(conversationId: string, body: string): Promise<ChatMessage> {
  const res = await api.post<ChatMessage>(`/me/conversations/${conversationId}/messages`, { body });
  return res.data;
}

export async function markConversationRead(conversationId: string): Promise<void> {
  await api.post(`/me/conversations/${conversationId}/read`);
}
